<script setup lang="ts">
import type { VImageTilesProps } from "./VImageTiles.types";
import { computed, ref } from "vue";
import VIcon from "../Icon/VIcon.vue";
import { useFieldId } from "../useFieldId";

defineOptions({
  name: "VImageTiles",
});

/**
 * Photos a person adds to a form, as 76px tiles with a remove button, followed by a dashed add tile. Without photos it
 * is one wide dashed "add photo" pill, 32px high like the other medium controls. It only shows the tiles and hands
 * over the chosen files: the caller checks, uploads and previews them.
 */
const {
  tiles,
  addLabel,
  emptyLabel,
  removeLabel,
  maxFiles = undefined,
  accept = undefined,
  capture = undefined,
  disabled = false,
  id: rawId = undefined,
  ariaDescribedby = undefined,
} = defineProps<VImageTilesProps>();

const emit = defineEmits<{
  /** The files the person chose, in order; empty selections are not emitted. */
  add: [files: File[]]
  /** The key of the tile whose remove button was pressed. */
  remove: [key: string | number]
}>();

const id = useFieldId(() => rawId);
const fileInput = ref<HTMLInputElement | null>(null);

const acceptAttribute = computed(() => (Array.isArray(accept) ? accept.join(",") : accept));
const remaining = computed(() =>
  (maxFiles === undefined ? Number.POSITIVE_INFINITY : Math.max(0, maxFiles - tiles.length)),
);
const canAdd = computed(() => remaining.value > 0);

function openPicker() {
  if (disabled) {
    return;
  }
  fileInput.value?.click();
}

function onChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  // Clearing the value lets the person pick the same file again after removing it.
  input.value = "";
  if (files.length === 0) {
    return;
  }
  emit("add", files);
}

const addTileClass = "flex items-center justify-center border border-dashed border-primary-600 bg-primary-50 font-medium text-primary-900 transition-colors hover:bg-primary-100 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-primary-400 dark:bg-primary-900/30 dark:text-primary-200 dark:hover:bg-primary-900/50";
</script>

<template>
  <div class="flex flex-wrap gap-2.5" data-testid="image-tiles">
    <input
      ref="fileInput"
      type="file"
      class="sr-only"
      tabindex="-1"
      aria-hidden="true"
      :accept="acceptAttribute"
      :capture="capture"
      :multiple="remaining > 1"
      :disabled="disabled"
      data-testid="image-tiles-input"
      @change="onChange"
    >
    <div
      v-for="(tile, index) in tiles"
      :key="tile.key"
      class="relative size-[76px]"
      :data-state="tile.state ?? 'ready'"
      data-testid="image-tile"
    >
      <img
        :src="tile.src"
        :alt="tile.errorMessage ? `${tile.alt}, ${tile.errorMessage}` : tile.alt"
        class="block size-[76px] rounded-xl border object-cover"
        :class="tile.state === 'error' ? 'border-danger-600' : 'border-gray-300 dark:border-dark-600'"
      >
      <span
        v-if="tile.state === 'uploading'"
        class="absolute inset-0 flex items-center justify-center rounded-xl bg-white/60 text-primary-600 dark:bg-dark-900/60 dark:text-primary-400"
        aria-hidden="true"
        data-testid="image-tile-uploading"
      >
        <VIcon name="loader-circle" spin class="text-xl" />
      </span>
      <span
        v-else-if="tile.state === 'error'"
        class="absolute bottom-1 left-1 flex size-6 items-center justify-center rounded-full bg-danger-600 text-xs text-white"
        :title="tile.errorMessage"
        aria-hidden="true"
        data-testid="image-tile-error"
      >
        <VIcon name="triangle-alert" />
      </span>
      <!-- A 44px hit area around the 26px circle, reaching past the top right corner of the tile. -->
      <button
        type="button"
        class="group absolute -top-2.5 -right-2.5 flex size-11 cursor-pointer items-start justify-end rounded-xl bg-transparent p-0 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-primary-500 disabled:cursor-not-allowed"
        :aria-label="removeLabel(index + 1)"
        :disabled="disabled"
        data-testid="image-tile-remove"
        @click="emit('remove', tile.key)"
      >
        <span class="mt-1 mr-1 flex size-[26px] items-center justify-center rounded-full border border-gray-300 bg-white text-sm text-gray-900 shadow-sm group-hover:bg-gray-50 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-100 dark:group-hover:bg-dark-700">
          <VIcon name="x" aria-hidden="true" />
        </span>
      </button>
    </div>
    <button
      v-if="tiles.length === 0"
      :id="id"
      type="button"
      class="h-8 w-full gap-2 rounded-full text-sm leading-5"
      :class="addTileClass"
      :disabled="disabled"
      :aria-describedby="ariaDescribedby"
      data-testid="image-tiles-add-wide"
      @click="openPicker"
    >
      <VIcon name="camera" class="text-sm" aria-hidden="true" />
      {{ emptyLabel }}
    </button>
    <button
      v-else-if="canAdd"
      :id="id"
      type="button"
      class="size-[76px] flex-col gap-1 rounded-xl text-xs leading-4"
      :class="addTileClass"
      :disabled="disabled"
      :aria-describedby="ariaDescribedby"
      data-testid="image-tiles-add"
      @click="openPicker"
    >
      <VIcon name="plus" class="text-lg" aria-hidden="true" />
      {{ addLabel }}
    </button>
  </div>
</template>
